import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Button,
	Card,
	DatePicker,
	Form,
	Input,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
	Upload,
	message,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import { antdKoKRSunday as locale } from "../../utilities/weekLocale";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

const { TextArea } = Input;

const HolidayHalfDayManage = () => {
	const isMobile = useIsMobile();
	const [rows, setRows] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form] = Form.useForm();
	const [uploadForm] = Form.useForm();

	const loadCredits = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/utils/leave/holiday-half-day/");
			setRows(Array.isArray(res.data) ? res.data : []);
		} catch {
			message.error("공휴일 반차 목록을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadEmployees = useCallback(async () => {
		try {
			const res = await useAxios.get("/utils/leave/annual-balance/");
			setEmployees(res.data?.employees || []);
		} catch {
			setEmployees([]);
		}
	}, []);

	useEffect(() => {
		loadCredits();
		loadEmployees();
	}, [loadCredits, loadEmployees]);

	const openCreate = () => {
		form.resetFields();
		setModalOpen(true);
	};

	const saveCredits = async () => {
		try {
			const values = await form.validateFields();
			setSaving(true);
			await useAxios.post("/utils/leave/holiday-half-day/", {
				user_ids: values.user_ids,
				reason: values.reason,
				reason_date: values.reason_date.format("YYYY-MM-DD"),
				expires_on: values.expires_on.format("YYYY-MM-DD"),
			});
			message.success("공휴일 반차를 추가했습니다.");
			setModalOpen(false);
			loadCredits();
		} catch (err) {
			if (err?.errorFields) return;
			message.error(
				err?.response?.data?.error || "추가에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const uploadExcel = async () => {
		try {
			const values = await uploadForm.validateFields();
			const file = values.file?.[0]?.originFileObj;
			if (!file) {
				message.error("엑셀 파일을 선택하세요.");
				return;
			}
			const formData = new FormData();
			formData.append("file", file);
			setSaving(true);
			const res = await useAxios.post(
				"/utils/leave/holiday-half-day/",
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } },
			);
			message.success(`${res.data?.created || 0}건이 등록되었습니다.`);
			const errors = res.data?.errors || [];
			if (errors.length) {
				message.warning(
					`오류 ${errors.length}건: ${errors
						.slice(0, 3)
						.map((e) => `${e.row}행 ${e.error}`)
						.join(" / ")}`,
				);
			}
			setUploadOpen(false);
			uploadForm.resetFields();
			loadCredits();
		} catch (err) {
			if (err?.errorFields) return;
			message.error(
				err?.response?.data?.error || "업로드에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const deleteCredit = async (record) => {
		try {
			await useAxios.delete("/utils/leave/holiday-half-day/", {
				data: { id: record.id },
			});
			message.success("삭제했습니다.");
			loadCredits();
		} catch (err) {
			message.error(
				err?.response?.data?.error || "삭제에 실패했습니다.",
			);
		}
	};

	const columns = [
		{ title: "이름", dataIndex: "user_name", width: 100 },
		{ title: "사유", dataIndex: "reason", ellipsis: true },
		{ title: "사유발생일", dataIndex: "reason_date", width: 120 },
		{ title: "사용가능일", dataIndex: "usable_from", width: 120 },
		{ title: "사용기한", dataIndex: "expires_on", width: 120 },
		{
			title: "상태",
			key: "status",
			width: 100,
			render: (_, record) => {
				if (record.is_used) return <Tag color="blue">사용</Tag>;
				if (record.is_expired) return <Tag>만료</Tag>;
				return <Tag color="green">사용가능</Tag>;
			},
		},
		{ title: "등록자", dataIndex: "created_by_name", width: 100 },
		{
			title: "작업",
			key: "actions",
			width: 90,
			render: (_, record) =>
				record.is_used ? null : (
					<Popconfirm
						title="삭제할까요?"
						okText="삭제"
						cancelText="취소"
						onConfirm={() => deleteCredit(record)}>
						<Button type="link" danger>
							삭제
						</Button>
					</Popconfirm>
				),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="공휴일 반차 관리" />
			<div className="page">
				<PageHeader
					title="공휴일 반차 관리"
					actions={
						<Space wrap>
							<Button
								icon={<UploadOutlined />}
								onClick={() => setUploadOpen(true)}>
								엑셀 업로드
							</Button>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								onClick={openCreate}>
								추가
							</Button>
						</Space>
					}
				/>
				<Card className="surface-card" loading={loading}>
					<Table
						rowKey="id"
						columns={columns}
						dataSource={rows}
						pagination={{ pageSize: 50 }}
						sticky={{ offsetHeader: 64 }}
						scroll={{ x: "max-content" }}
					/>
				</Card>
			</div>

			<Modal
				title="공휴일 반차 추가"
				open={modalOpen}
				onOk={saveCredits}
				onCancel={() => setModalOpen(false)}
				confirmLoading={saving}
				okText="저장"
				cancelText="취소"
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="user_ids"
						label="대상자"
						rules={[
							{ required: true, message: "대상자를 선택하세요" },
						]}>
						<Select
							mode="multiple"
							showSearch
							optionFilterProp="label"
							placeholder="이름 검색"
							options={employees.map((emp) => ({
								value: emp.user_id,
								label: emp.name,
							}))}
						/>
					</Form.Item>
					<Form.Item
						name="reason"
						label="사유"
						rules={[{ required: true, message: "사유를 입력하세요" }]}>
						<TextArea rows={3} />
					</Form.Item>
					<Form.Item
						name="reason_date"
						label="사유발생일"
						rules={[
							{ required: true, message: "사유발생일을 선택하세요" },
						]}>
						<DatePicker inputReadOnly={isMobile} style={{ width: "100%" }} />
					</Form.Item>
					<Form.Item
						name="expires_on"
						label="사용기한"
						rules={[
							{ required: true, message: "사용기한을 선택하세요" },
						]}>
						<DatePicker inputReadOnly={isMobile} style={{ width: "100%" }} />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="엑셀 일괄 추가"
				open={uploadOpen}
				onOk={uploadExcel}
				onCancel={() => setUploadOpen(false)}
				confirmLoading={saving}
				okText="업로드"
				cancelText="취소"
				destroyOnClose>
				<p style={{ marginBottom: 12 }}>
					열: 이름, 사유, 사유발생일, 사용기한(일)
				</p>
				<Form form={uploadForm} layout="vertical">
					<Form.Item
						name="file"
						label="엑셀 파일"
						valuePropName="fileList"
						getValueFromEvent={(e) =>
							Array.isArray(e) ? e : e?.fileList
						}
						rules={[
							{ required: true, message: "파일을 선택하세요" },
						]}>
						<Upload
							beforeUpload={() => false}
							maxCount={1}
							accept=".xlsx,.xls">
							<Button icon={<UploadOutlined />}>파일 선택</Button>
						</Upload>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default HolidayHalfDayManage;
